import { Button } from "@/components/ui/button";
import { fmt, calcMonthlyPayment } from "@/hooks/useVehicleSim";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

interface Props {
  loanAmount: number;
  creditScore: number;
  aprMap: Record<number, number>;
  selectedTerm: number;
  onTermChange: (term: number) => void;
  onContinue: () => void;
  onBack: () => void;
}

const TERMS = [36, 48, 60, 72];

function riskLabel(apr: number): { text: string; color: string } {
  if (apr < 0.06) return { text: 'Low Risk', color: 'text-emerald-600' };
  if (apr < 0.08) return { text: 'Moderate', color: 'text-amber-600' };
  if (apr < 0.11) return { text: 'Elevated', color: 'text-orange-600' };
  return { text: 'High Risk', color: 'text-destructive' };
}

export default function FinancingScreen({ loanAmount, creditScore, aprMap, selectedTerm, onTermChange, onContinue, onBack }: Props) {
  const creditLabel = creditScore >= 750 ? 'Excellent' : creditScore >= 700 ? 'Good' : creditScore >= 650 ? 'Fair' : 'Needs Improvement';

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="font-heading text-lg font-bold text-foreground">Financing Options</h2>
        <p className="text-sm text-muted-foreground">Your credit score of <span className="font-semibold text-foreground">{creditScore}</span> ({creditLabel}) determines your available rates.</p>
      </div>

      <div className="space-y-3">
        {TERMS.map(term => {
          const apr = aprMap[term] || 0.069;
          const monthly = calcMonthlyPayment(loanAmount, apr, term);
          const totalPaid = monthly * term;
          const totalInterest = totalPaid - loanAmount;
          const risk = riskLabel(apr);
          const isSelected = selectedTerm === term;

          return (
            <button
              key={term}
              onClick={() => onTermChange(term)}
              className={`w-full text-left p-4 rounded-2xl border transition-all duration-200 ${
                isSelected
                  ? 'glass-card glow-border ring-2 ring-primary/30'
                  : 'glass-card hover:shadow-card-hover'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-heading font-bold text-foreground">{term} Months</span>
                  <span className={`text-[10px] font-semibold ${risk.color}`}>{risk.text}</span>
                </div>
                {isSelected && <Check className="w-4 h-4 text-primary" />}
              </div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <p className="text-muted-foreground">APR</p>
                  <p className="font-bold text-foreground">{(apr * 100).toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Monthly</p>
                  <p className="font-bold text-foreground">{fmt(monthly)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Total Interest</p>
                  <p className="font-bold text-foreground">{fmt(totalInterest)}</p>
                </div>
              </div>
              <div className="mt-2 text-[10px] text-muted-foreground">Total paid: {fmt(totalPaid)}</div>
            </button>
          );
        })}
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="gap-2"><ArrowLeft className="w-4 h-4" /> Back</Button>
        <Button onClick={onContinue} className="flex-1 gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
